import { useCallback, useEffect, useReducer, useState } from 'react';
import { arrayFilter, arrayPages } from '../../../../../../common/filtering';
import { FetchRefreshFunctionParams } from '../../../../../../common/useFetch';
import useListFetch from '../../../../../../common/useListFetch';
import CmonProxysqlService from '../../../../../../services/cmon/CmonProxysqlService';
import {
    initialListState,
    listReducer,
    setListAll,
} from '../../../../../../common/listReducer';
import { getSortAlphabeticFn } from '../../../../../../common/sorting';
import { TableData, handleCcProxyVariables } from './proxySQLVariablesHelper';

type ListParams = {
    page?: number;
    pageSize?: number;
    order?: (a: any, b: any) => any;
    filters?: Function[];
};

type FilterFunctionParams = ListParams & { arr?: any[] };
type RefreshFunctionParams = FetchRefreshFunctionParams;

export type UseProxySQLVariablesListProps = {
    pageSize?: number;
    clusterId: number | undefined;
    hostName: string | undefined;
    port: number | undefined;
    filters?: Function[];
};
export default function useProxySQLVariables({
    pageSize = 15,
    clusterId,
    filters,
    hostName,
    port,
}: UseProxySQLVariablesListProps) {
    const [list, setList] = useState<TableData[]>();
    const [total, setTotal] = useState<number>(0);

    const {
        loading,
        loaded,
        list: variablesList,
        refresh: refreshVariables,
    } = useListFetch({
        name: 'queryVariables',
        pageSize,
        fetchFn: async ({ pageSize, page, ...rest }, opts) => {
            const response = await CmonProxysqlService.queryvariables(
                {
                    cluster_id: clusterId,
                    hostName: hostName,
                    port: port,
                    ...rest,
                },
                opts
            );
            setTotal(response.queryResultTotalCount);
            return {
                list: handleCcProxyVariables(response.queryResults),
                total: response.queryResultTotalCount,
            };
        },
        cancelFn: async ({ requestId }) => {
            await CmonProxysqlService.cancelRequest(requestId);
        },
    });

    const [
        {
            page: listPage,
            pageSize: listPageSize,
            order: listOrder,
            filters: listFilters,
        },
        listDispatch,
    ] = useReducer(listReducer, {
        ...initialListState,
        pageSize: pageSize || 0,
        filters: filters,
        order: getSortAlphabeticFn('ascend', (x) => x.variableName),
    });

    const filter = useCallback<(p?: FilterFunctionParams) => void>(
        ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            arr = variablesList || [],
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            const filteredArr = arrayFilter({
                filters: [...(filters || [])],
                arr,
            });

            setList(
                arrayPages({
                    page,
                    pageSize,
                    order,
                    arr: filteredArr,
                })
            );

            setTotal(filteredArr.length);
        },

        [listPage, listPageSize, listOrder, listFilters, variablesList]
    );
    const refresh = useCallback<(p?: RefreshFunctionParams) => Promise<void>>(
        async ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            ...rest
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            await refreshVariables({
                ...rest,
            });
        },
        []
    );

    useEffect(() => {
        if (Array.isArray(variablesList)) {
            filter({
                arr: variablesList,
            });
        }
    }, [variablesList]);

    return {
        list,
        loading,
        loaded,
        refresh,
        filter,
        page: listPage,
        pageSize: listPageSize,
        total,
    };
}
